import Component from "../component";
import { loadScript } from "../../utils/load_script";
import GeolocateComponent, { GeolocateComponentEvents } from "./geolocate";
import { GenericPosition, GeolocationService } from "../../services/geolocation";
import { loadCss } from "../../utils/load_css";
import { replace } from "../../utils/utils";
import { getLocale, getLocaleLang } from "../../helpers/locale";
import { getConfig } from "../../configuration/config";
import { WoosmapApiClient } from "../../services/woosmap_api";
import { GeoLocationResponse } from "types/geolocation";

export interface SearchLocation {
    name: string;
    location: woosmap.map.LatLngLiteral;
}

export interface ISearchComponent {
    inputID: string;
    woosmapPublicKey: string;
    searchOptions: woosmap.localities.AutocompleteParameters;
    selectedLocality?: SearchLocation;
    featuresBtn: string[];
}

export enum SearchComponentEvents {
    SELECTED_LOCALITY = "selected_locality",
    SEARCH_CLEAR = "search_clear",
    WOOS_GEOLOCATION_SET = "woos_geolocation_set",
}

export default class SearchComponent extends Component<ISearchComponent> {
    private localitiesWidget!: woosmap.localities.Autocomplete;

    init(): void {
        this.$element = <HTMLInputElement>document.createElement("input");
        this.$element.className = "search__input";
        this.$element.setAttribute("placeholder", getLocale().search.placeholder);
        this.$element.setAttribute("aria-label", getLocale().stores.messages.initialHeader);
        this.$element.setAttribute("autocomplete", "off");
        this.$element.id = this.state.inputID;
        this.$target.appendChild(this.$element);
    }

    render(): void {
        if (this.state && this.$element) {
            loadScript({
                url: getConfig().urls.localitiesWidgetJS,
                params: { key: this.state.woosmapPublicKey }
            })
                .then(() => loadCss(getConfig().urls.localitiesWidgetCSS))
                .then(() => this.initSearchView())
                .catch((error) => {
                    console.error(
                        replace(getLocale().errors.loadingLibrary, { 'library': "Woosmap Localities JS Widget" }),
                        error
                    );
                });
        }
    }

    initSearchView(): void {
        let searchOptions = this.state.searchOptions;
        GeolocationService.isHTMLGeoLocationPermissionGranted().then((permissionGranted) => {
            const initializeWidget = (latitude?: number, longitude?: number) => {
                if (latitude !== undefined && longitude !== undefined) {
                    searchOptions = { ...this.state.searchOptions, location: { lat: latitude, lng: longitude }, language: getLocaleLang().toLowerCase() };
                } else {
                    searchOptions = { ...this.state.searchOptions, language: getLocaleLang().toLowerCase() };
                }
                this.localitiesWidget = new woosmap.localities.Autocomplete(
                    this.state.inputID,
                    searchOptions
                );
                const $inputContainer = (document.getElementById(this.state.inputID) as HTMLElement).parentElement as HTMLElement;

                this.state.featuresBtn.forEach((feature: string) => {
                    if (feature === 'search') {
                        this.manageSearchButton($inputContainer);
                    }
                    if (feature === 'clear') {
                        this.manageClearButton($inputContainer);
                    }
                    if (feature === 'geolocate') {
                        this.manageGeolocateButton($inputContainer);
                    }
                });
                this.localitiesWidget.addListener("selected_suggestion", () => {
                    const locality: woosmap.localities.DetailsResponseItem = this.localitiesWidget.getSelectedSuggestionDetails();
                    const searchLocation: SearchLocation = {
                        name: locality.name || locality.formatted_address,
                        location: locality.geometry.location
                    }
                    this.setState({ selectedLocality: searchLocation }, true)
                    this.emit(SearchComponentEvents.SELECTED_LOCALITY, searchLocation);
                });
            };

            if (permissionGranted) {
                this.getHTMLGeoLocation().then(pos => {
                    initializeWidget(pos.coords.latitude, pos.coords.longitude);
                }).catch(error => console.log(error));
            } else {
                this.getWoosGeoLocation().then(pos => {
                    if (pos.accuracy <= getConfig().search.geoLocationAccuracyThreshold) {
                        initializeWidget(pos.latitude, pos.longitude);
                        this.emit(SearchComponentEvents.WOOS_GEOLOCATION_SET, pos);
                    } else {
                        this.getHTMLGeoLocation().then(pos => {
                            initializeWidget(pos.coords.latitude, pos.coords.longitude);
                        }).catch(error => {
                            // Fallback to initializing widget without location
                            initializeWidget();
                        });
                    }
                }).catch(error => {
                    // Fallback to initializing widget without location
                    initializeWidget();
                });
            }
        });

        if (this.state.selectedLocality) {
            this.setLocality(this.state.selectedLocality.name || '');
            this.emit(SearchComponentEvents.SELECTED_LOCALITY, this.state.selectedLocality);
        }
    }

    manageSearchButton($inputContainer: HTMLElement): void {
        const $searchBtn = document.createElement("div");
        $searchBtn.className = "search__searchBtn";
        $searchBtn.setAttribute("role", "button");
        $searchBtn.setAttribute("tabindex", "0");
        $searchBtn.innerHTML = `<button type="button" tabindex="-1" aria-label="${getLocale().search.submitLabel}"></button>`
        $searchBtn.addEventListener("click", () => {
            if (this.state.selectedLocality) {
                this.emit(SearchComponentEvents.SELECTED_LOCALITY, this.state.selectedLocality);
            } else {
                this.$element.focus();
            }
        });
        $inputContainer.appendChild($searchBtn);
    }

    manageClearButton($inputContainer: HTMLElement): void {
        const $clearBtn = document.createElement("div");
        $clearBtn.className = "search__clearBtn";
        $clearBtn.setAttribute("role", "button");
        $clearBtn.setAttribute("tabindex", "0");
        $clearBtn.innerHTML = `<button type="button" tabindex="-1" aria-label="${getLocale().search.clearLabel}"></button>`
        $clearBtn.addEventListener("keydown", (event) => {
            if (event.key === "Enter" || event.key === " ") {

                const $emptyButton = $inputContainer.querySelector(".localities-empty-button") as HTMLDivElement
                const geolocateButton = document.querySelector(".search__geolocateBtn") as HTMLElement;
                if ($emptyButton) {
                    $emptyButton.click();
                    $clearBtn.style.display = 'none';
                    if (geolocateButton) {
                        geolocateButton.style.display = "block";
                    }
                } else {
                    (this.$element as HTMLInputElement).value = "";
                    $clearBtn.style.display = 'none';
                    if (geolocateButton) {
                        geolocateButton.style.display = "block";
                    }
                    this.$element.focus();
                }
                this.emit(SearchComponentEvents.SEARCH_CLEAR);
            }


        });
        $clearBtn.addEventListener("click", (event) => {
            const $emptyButton = $inputContainer.querySelector(".localities-empty-button") as HTMLDivElement
            const geolocateButton = document.querySelector(".search__geolocateBtn") as HTMLElement;
            if ($emptyButton) {
                $emptyButton.click();
                $clearBtn.style.display = 'none';
                if (geolocateButton) {
                    geolocateButton.style.display = "block";
                }
            } else {
                (this.$element as HTMLInputElement).value = "";
                $clearBtn.style.display = 'none';
                if (geolocateButton) {
                    geolocateButton.style.display = "block";
                }
                this.$element.focus();
            }
            this.emit(SearchComponentEvents.SEARCH_CLEAR);
        });
        ['input', 'locality_changed'].forEach(evt =>
            this.$element.addEventListener(evt, () => {
                const geolocateButton = document.querySelector(".search__geolocateBtn") as HTMLElement;
                if ((this.$element as HTMLInputElement).value.length === 0) {
                    $clearBtn.style.display = 'none';
                } else {
                    $clearBtn.style.display = 'block';
                    if (geolocateButton) {
                        geolocateButton.style.display = "none";
                    }
                }
            })
        );
        $clearBtn.style.display = 'none';
        $inputContainer.appendChild($clearBtn);
    }

    manageGeolocateButton($inputContainer: HTMLElement): void {
        const geolocateComponent = new GeolocateComponent({
            $target: $inputContainer,
            initialState: {},
        });
        geolocateComponent.on(GeolocateComponentEvents.GEOLOCATION_FOUND, (geolocation: GenericPosition) => {
            if (geolocation) {
                const searchLocation: SearchLocation = {
                    name: geolocation.name,
                    location: {
                        lat: geolocation.latitude,
                        lng: geolocation.longitude,
                    }
                }
                this.setLocality(getLocale().search.yourLocation);
                this.setState({ selectedLocality: searchLocation }, true)
                this.emit(SearchComponentEvents.SELECTED_LOCALITY, searchLocation);
            }
        })
    }


    loadWidgetStylesheet(): void {
        const newLink: HTMLLinkElement = document.createElement("link");
        newLink.rel = "stylesheet";
        newLink.href = getConfig().urls.localitiesWidgetCSS;
        document.head.insertBefore(newLink, document.head.firstElementChild);
    }

    setLocality(name: string): void {
        (this.$element as HTMLInputElement).value = name;
        (this.$element as HTMLInputElement).dispatchEvent(new Event('locality_changed'))
    }

    async getWoosGeoLocation(): Promise<GeoLocationResponse> {
        const params = Object.assign({});
        const api = new WoosmapApiClient({ apiKey: getConfig().map.woosmapPublicKey });
        const geoLocationResponse = await api.geoLocation.getCurrentPosition(params);
        return geoLocationResponse;
    }
    async getHTMLGeoLocation(): Promise<GeolocationPosition> {
        return await GeolocationService.getCurrentPosition();
    }
}
